import { getSupabase } from "./supabaseClient";
import { computePetPower } from "./SitePetPower";

const USER_KEY = "portfolio_pet_user_id";
const CARD_KEY = "portfolio_pet_card";
const STATS_KEY = "portfolio_pet_stats";
const VISIT_SESSION_KEY = "portfolio_pet_visit_recorded_session";

let lastActiveTimeAt = 0;

const emptyStats = () => ({
  visits: 0,
  clicks: 0,
  scrolls: 0,
  drags: 0,
  jumps: 0,
  timeMs: 0,
  powerups: 0,
});

const canTrackActiveTime = () => {
  if (typeof document === "undefined") return false;
  if (document.hidden || document.visibilityState !== "visible") return false;
  if (typeof document.hasFocus === "function" && !document.hasFocus()) return false;
  return true;
};

export const getPetUserId = () => {
  let id = localStorage.getItem(USER_KEY);

  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(USER_KEY, id);
  }

  return id;
};

export const getStoredPetCard = () => {
  try {
    return JSON.parse(localStorage.getItem(CARD_KEY));
  } catch {
    return null;
  }
};

export const savePetCard = (card) => {
  const saved = { ...card, visitorId: getPetUserId() };

  localStorage.setItem(CARD_KEY, JSON.stringify(saved));

  window.dispatchEvent(
    new CustomEvent("portfolio-pet-card-change", { detail: saved })
  );

  return saved;
};

export const getPetStats = () => {
  try {
    return { ...emptyStats(), ...JSON.parse(localStorage.getItem(STATS_KEY)) };
  } catch {
    return emptyStats();
  }
};

export const savePetStats = (stats) => {
  localStorage.setItem(STATS_KEY, JSON.stringify(stats));
  return stats;
};

export const recordPetEvent = (type, amount = 1) => {
  const stats = getPetStats();

  if (type === "timeMs") {
    if (!canTrackActiveTime()) {
      lastActiveTimeAt = 0;
      return stats;
    }

    stats.timeMs += Math.max(0, Math.min(amount, 5000));
    return savePetStats(stats);
  }

  if (type === "visit") stats.visits += amount;
  if (type === "click") stats.clicks += amount;
  if (type === "scroll") stats.scrolls += amount;
  if (type === "drag") stats.drags += amount;
  if (type === "jump") stats.jumps += amount;
  if (type === "powerup") stats.powerups += amount;

  return savePetStats(stats);
};

export const recordActivePetTime = (maxChunkMs = 1000) => {
  if (!canTrackActiveTime()) {
    lastActiveTimeAt = 0;
    return getPetStats();
  }

  const now = performance.now();

  if (!lastActiveTimeAt) {
    lastActiveTimeAt = now;
    return getPetStats();
  }

  const chunk = Math.min(now - lastActiveTimeAt, maxChunkMs);
  lastActiveTimeAt = now;

  if (chunk < 250) return getPetStats();

  return recordPetEvent("timeMs", Math.round(chunk));
};

export const subscribeToPetCardChange = (callback) => {
  const handler = (event) => callback(event.detail || getStoredPetCard());

  window.addEventListener("portfolio-pet-card-change", handler);
  window.addEventListener("storage", handler);

  return () => {
    window.removeEventListener("portfolio-pet-card-change", handler);
    window.removeEventListener("storage", handler);
  };
};

export async function syncPetToSupabase(cardOverride = null) {
  const supabase = getSupabase();
  const card = cardOverride || getStoredPetCard();

  if (!supabase || !card) return;

  const stats = getPetStats();

  const cardVariant = card.cardVariant || 1;
  const cardImagePath =
    card.cardImagePath || `${card.characterId || "trex"}/card${cardVariant}.png`;

  const payload = {
    visitor_id: getPetUserId(),
    card_id: card.id || crypto.randomUUID(),
    character_id: card.characterId,
    character_name: card.characterName,
    personality: card.personality,
    rarity: card.rarity,
    rarity_label: card.rarityLabel || card.rarity,
    card_number: card.cardNumber,
    card_code:
      card.cardCode ||
      `${String(card.characterId || "trex").toUpperCase()}-${String(
        card.rarity || "emerald"
      ).toUpperCase()}-${String(cardVariant).padStart(2, "0")}`,
    card_variant: cardVariant,
    card_image_path: cardImagePath,
    flavor_line: card.flavorLine || card.flavor_line || "",
    signature: card.signature || "",
    traits: card.traits || [],
    card_stats: card.stats || {},
    base_power: card.basePower || 0,
    live_stats: stats,
    power: computePetPower(card, stats),
    created_at: card.createdAt || new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };

  await supabase.from("portfolio_pet_cards").upsert(payload, {
    onConflict: "visitor_id",
  });
}


export async function fetchAllPetCards() {
  const supabase = getSupabase();

  if (!supabase) return [];

  const { data, error } = await supabase
    .from("portfolio_pet_cards")
    .select("*")
    .order("created_at", { ascending: false });

  if (error) {
    console.error("Cards showcase failed:", error.message);
    return [];
  }

  return data || [];
}

export const recordPetVisitOnce = () => {
  if (sessionStorage.getItem(VISIT_SESSION_KEY) === "true") {
    return getPetStats();
  }

  sessionStorage.setItem(VISIT_SESSION_KEY, "true");
  return recordPetEvent("visit");
};
